export interface TikTokPixelItem {
  id: string | number
  quantity?: number
  /** Minor currency units (e.g. cents), matching Vendure's Money type. */
  price?: number
}

export interface TikTokPixelCustomerData {
  email?: string
  phone?: string
}

// `orderTotal` (minor units) should be the order's actual charged total —
// summing per-line list prices ignores coupons/promotions and would over-report
// `value` to the ad account whenever a discount is active. Item-level prices in
// `contents[]` intentionally stay list price, for catalog matching.
function itemsProperties(items: TikTokPixelItem[], currency?: string, orderTotal?: number) {
  const summedValue = items.reduce((sum, item) => sum + ((item.price ?? 0) / 100) * (item.quantity ?? 1), 0)
  const value = orderTotal != null ? orderTotal / 100 : summedValue

  return {
    content_type: 'product',
    currency,
    value: Math.round(value * 100) / 100,
    quantity: items.reduce((sum, item) => sum + (item.quantity ?? 1), 0),
    contents: items.map(item => ({
      content_id: String(item.id),
      content_type: 'product',
      quantity: item.quantity ?? 1,
      price: item.price != null ? item.price / 100 : undefined,
    })),
  }
}

export function useTikTokPixel() {
  // `tiktokPixel` is only declared in apps that opt in (see apps/exalted.com/app/config/runtime.ts),
  // so it's untyped for every other app extending this layer — cast like the Meta Pixel composable.
  const config = useRuntimeConfig() as any
  const enabled = computed(() => !!config.public?.tiktokPixelId)

  function track(eventName: string, properties?: Record<string, unknown>, userData?: TikTokPixelCustomerData, dedupeId?: string) {
    if (!enabled.value)
      return

    // Terminal, order-level events (CompletePayment) pass a deterministic id derived
    // from the order code so a confirmation-page reload or poll re-run dedupes against
    // the first send — both browser↔Events-API and across page loads. Repeatable events
    // (ViewContent/AddToCart/…) get a fresh id so legitimate repeats aren't dropped.
    const eventId = dedupeId ?? crypto.randomUUID()

    window.ttq?.track(eventName, properties, { event_id: eventId })

    $fetch('/api/tiktok-events', {
      method: 'POST',
      // Survive a navigation away mid-request — without this the browser
      // cancels the send and the event is silently lost.
      keepalive: true,
      body: {
        eventName,
        eventId,
        eventSourceUrl: window.location.href,
        properties,
        userData,
      },
    }).catch(() => { /* best-effort telemetry, never surface to the UI */ })
  }

  return {
    enabled,
    track,
    viewContent: (item: TikTokPixelItem, currency?: string) =>
      track('ViewContent', itemsProperties([item], currency)),
    addToCart: (item: TikTokPixelItem, currency?: string) =>
      track('AddToCart', itemsProperties([item], currency)),
    initiateCheckout: (items: TikTokPixelItem[], currency?: string, orderTotal?: number, userData?: TikTokPixelCustomerData) =>
      track('InitiateCheckout', itemsProperties(items, currency, orderTotal), userData),
    addPaymentInfo: (items: TikTokPixelItem[], currency?: string, orderTotal?: number, userData?: TikTokPixelCustomerData) =>
      track('AddPaymentInfo', itemsProperties(items, currency, orderTotal), userData),
    // TikTok's standard event for a completed order is "CompletePayment", not "Purchase".
    purchase: (items: TikTokPixelItem[], currency?: string, orderTotal?: number, orderId?: string, userData?: TikTokPixelCustomerData) =>
      track('CompletePayment', itemsProperties(items, currency, orderTotal), userData, orderId ? `CompletePayment-${orderId}` : undefined),
  }
}
